// Global baseline: the smallest swap notional (USD) we will execute on any
// chain unless that chain overrides it below. Also the absolute floor a user's
// Rebalance Threshold Override may never drop below.
export const REBALANCE_THRESHOLD_USD_DEFAULT = "1.00";
export const REBALANCE_THRESHOLD_USD_MIN = "1.00";
export const RECURRING_TRANSFER_THRESHOLD_USD_MIN = "0.75";

// Per-chain minimum swap notional (USD) — the single source of truth for
// "the smallest swap allowed on a given chain". Only list chains that DIFFER
// from REBALANCE_THRESHOLD_USD_MIN ($1); everything not listed (Base, BNB,
// Solana, …) inherits the $1 default. To change a chain's floor, edit this map.
// A user's per-portfolio Rebalance Threshold Override is clamped UP to this
// value (see resolveEffectiveUserSwapPreferences) and can never go below it.
export const REBALANCE_THRESHOLD_USD_CHAIN_MIN: Record<number, string> = {
  1: "10.00", // Ethereum L1 — higher gas, so a higher swap floor
};

// Per-chain default threshold surfaced to users before they set an override.
// Mirror REBALANCE_THRESHOLD_USD_CHAIN_MIN unless a chain wants a default above
// its hard floor.
export const REBALANCE_THRESHOLD_USD_CHAIN_DEFAULT: Record<number, string> = {
  1: "10.00",
};

export const SLIPPAGE_BPS_MIN = 0;
export const SLIPPAGE_BPS_MAX = 1_000;
export const SLIPPAGE_BPS_DEFAULT = 500;

export const PRICE_IMPACT_BPS_MIN = 0;
export const PRICE_IMPACT_BPS_MAX = 1_000;
export const PRICE_IMPACT_BPS_DEFAULT = 500;

export const SLIPPAGE_BPS_RELAXED_FLOOR = 750;
export const PRICE_IMPACT_BPS_RELAXED = 1_000;

export const SWEEP_MAX_OVERAGE_BPS_DEFAULT = 10_500;
export const EXIT_DUST_FLOOR_USD_DEFAULT = REBALANCE_THRESHOLD_USD_MIN;

const SLIPPAGE_BPS_ASSET_MIN_RAW = {
  "0xc796e499cc8f599a2a8280825d8bda92f7a895e0:8453": 500,
  "0x3977fc913db86b01a257232c568317798b903b07:8453": 500,
  "0x00000e7efa313f4e11bfff432471ed9423ac6b30:8453": 500,
  "0x69da011296a3a68d33bcfbca078e0be3b7d77b07:8453": 500,
  "0x6dd4e9e85b96d4cb8a0c16c8a9b097e1c9205617:8453": 500,
  "0x64cc19a52f4d631ef5be07947caba14ae00c52eb:8453": 500,
  "0x1f015712aa2a48085ec93f87d643bb625b668b07:8453": 500,
  "0x051024b653e8ec69e72693f776c41c2a9401fb07:8453": 500,
  "0xc3679395bddfb080fed2e26a54ab224dc582c99a:8453": 500,
  "0xef5997c2cf2f6c138196f8a6203afc335206b3c1:8453": 500,
  "0x5fc2843838e65eb0b5d33654628f446d54602791:8453": 500,
  "0x09fc6e5ad1a1e074d017ec9a6d677f31f97efb07:8453": 500,
} as const;

export const SLIPPAGE_BPS_ASSET_MIN: Record<string, number> =
  SLIPPAGE_BPS_ASSET_MIN_RAW;

export type UserSwapPreferencesPatch = {
  thresholdUsd?: string;
  slippageBps?: number;
  priceImpactBps?: number;
};

export type UserSwapPreferencesResolved = {
  thresholdUsd: string;
  slippageBps: number;
  priceImpactBps: number;
};

export type UserSwapPreferencesFieldSource =
  | "default"
  | "userGlobal"
  | "portfolio"
  | "chainFloor";

// TODO(user-swap-preferences): Add a DEX-specific preference layer in the
// precedence hierarchy (for example, per-DEX slippage/price-impact behavior).

export type UserSwapPreferencesResolvedWithMetadata = {
  defaults: UserSwapPreferencesResolved;
  userGlobalOverride: UserSwapPreferencesPatch | null;
  portfolioOverride: UserSwapPreferencesPatch | null;
  resolved: UserSwapPreferencesResolved;
  source: {
    thresholdUsd: UserSwapPreferencesFieldSource;
    slippageBps: UserSwapPreferencesFieldSource;
    priceImpactBps: UserSwapPreferencesFieldSource;
  };
  chain: {
    chainId: number | null;
    thresholdFloorUsd: string;
    floorApplied: boolean;
  };
};

export type UserSwapPreferencesValidationResult =
  | { isValid: true; normalizedPatch: UserSwapPreferencesPatch }
  | { isValid: false; errors: string[] };

export type RecurringSwapMinExecutionUsdSource = "default" | "chainFloor";

export type RecurringSwapMinExecutionUsdResolution = {
  chainId: number | null;
  minExecutionUsd: string;
  source: RecurringSwapMinExecutionUsdSource;
};

export type RecurringTransferMinExecutionUsdSource =
  RecurringSwapMinExecutionUsdSource;

export type RecurringTransferMinExecutionUsdResolution = {
  chainId: number | null;
  minExecutionUsd: string;
  source: RecurringTransferMinExecutionUsdSource;
};

const BPS_SCALE = 10_000;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const toUsdNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
};

const normalizeUsd = (value: string): string => {
  const parsed = Number(value.trim());
  return parsed.toFixed(2);
};

const normalizeOptionalChainId = (chainId?: number | null): number | null => {
  if (typeof chainId !== "number" || !Number.isFinite(chainId)) {
    return null;
  }
  return Math.trunc(chainId);
};

const normalizeAssetIdKey = (assetId: string): string => {
  const normalized = assetId.trim();
  return normalized.slice(0, 2).toLowerCase() === "0x"
    ? normalized.toLowerCase()
    : normalized;
};

const getThresholdDefaultForChain = (chainId?: number | null): string => {
  if (typeof chainId !== "number") {
    return REBALANCE_THRESHOLD_USD_DEFAULT;
  }
  return (
    REBALANCE_THRESHOLD_USD_CHAIN_DEFAULT[chainId] ??
    REBALANCE_THRESHOLD_USD_DEFAULT
  );
};

const getThresholdFloorForChain = (chainId?: number | null): string => {
  if (typeof chainId !== "number") {
    return REBALANCE_THRESHOLD_USD_MIN;
  }
  return (
    REBALANCE_THRESHOLD_USD_CHAIN_MIN[chainId] ??
    REBALANCE_THRESHOLD_USD_MIN
  );
};

/**
 * Canonical accessor for the minimum swap notional (USD) on a given chain — the
 * single source of truth callers should use instead of hardcoding "1.00"/"10".
 * Returns the chain's entry in REBALANCE_THRESHOLD_USD_CHAIN_MIN, or the $1
 * global default (REBALANCE_THRESHOLD_USD_MIN) for chains without an override.
 * This is the hard floor; a user's Rebalance Threshold Override may raise the
 * effective minimum above it but never below (see
 * resolveEffectiveUserSwapPreferences).
 */
export const getMinNotionalUsdForChain = (chainId?: number | null): string =>
  getThresholdFloorForChain(chainId);

const getRecurringTransferThresholdFloorForChain = (
  chainId?: number | null,
): string => {
  if (typeof chainId !== "number") {
    return RECURRING_TRANSFER_THRESHOLD_USD_MIN;
  }
  return (
    REBALANCE_THRESHOLD_USD_CHAIN_MIN[chainId] ??
    RECURRING_TRANSFER_THRESHOLD_USD_MIN
  );
};

export const resolveRecurringSwapMinExecutionUsd = (
  chainId?: number | null,
): RecurringSwapMinExecutionUsdResolution => {
  const normalizedChainId = normalizeOptionalChainId(chainId);
  const source: RecurringSwapMinExecutionUsdSource =
    normalizedChainId !== null &&
    REBALANCE_THRESHOLD_USD_CHAIN_MIN[normalizedChainId] !== undefined
      ? "chainFloor"
      : "default";

  return {
    chainId: normalizedChainId,
    minExecutionUsd: getThresholdFloorForChain(normalizedChainId),
    source,
  };
};

export const resolveRecurringTransferMinExecutionUsd = (
  chainId?: number | null,
): RecurringTransferMinExecutionUsdResolution => {
  const normalizedChainId = normalizeOptionalChainId(chainId);
  const source: RecurringTransferMinExecutionUsdSource =
    normalizedChainId !== null &&
    REBALANCE_THRESHOLD_USD_CHAIN_MIN[normalizedChainId] !== undefined
      ? "chainFloor"
      : "default";

  return {
    chainId: normalizedChainId,
    minExecutionUsd: getRecurringTransferThresholdFloorForChain(
      normalizedChainId,
    ),
    source,
  };
};

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export const toLifiDecimalSlippage = (slippageBps: number): number =>
  clamp(slippageBps, SLIPPAGE_BPS_MIN, SLIPPAGE_BPS_MAX) / BPS_SCALE;

export const toLifiDecimalPriceImpact = (priceImpactBps: number): number =>
  clamp(priceImpactBps, PRICE_IMPACT_BPS_MIN, PRICE_IMPACT_BPS_MAX) / BPS_SCALE;

export const validateUserSwapPreferencesPatch = (
  patch: UserSwapPreferencesPatch,
): UserSwapPreferencesValidationResult => {
  const errors: string[] = [];

  if (!patch || typeof patch !== "object") {
    return { isValid: false, errors: ["config patch must be an object"] };
  }

  const hasAnyField =
    patch.thresholdUsd !== undefined ||
    patch.slippageBps !== undefined ||
    patch.priceImpactBps !== undefined;

  if (!hasAnyField) {
    errors.push(
      "config patch must include at least one of thresholdUsd, slippageBps, priceImpactBps",
    );
  }

  const normalizedPatch: UserSwapPreferencesPatch = {};

  if (patch.thresholdUsd !== undefined) {
    if (typeof patch.thresholdUsd !== "string") {
      errors.push("thresholdUsd must be a decimal string");
    } else {
      const parsed = toUsdNumber(patch.thresholdUsd);
      if (parsed === null) {
        errors.push("thresholdUsd must be a valid decimal string");
      } else if (parsed < Number(REBALANCE_THRESHOLD_USD_MIN)) {
        errors.push(
          `thresholdUsd must be >= ${REBALANCE_THRESHOLD_USD_MIN}`,
        );
      } else {
        normalizedPatch.thresholdUsd = normalizeUsd(patch.thresholdUsd);
      }
    }
  }

  if (patch.slippageBps !== undefined) {
    if (!isFiniteNumber(patch.slippageBps) || !Number.isInteger(patch.slippageBps)) {
      errors.push("slippageBps must be an integer");
    } else if (
      patch.slippageBps < SLIPPAGE_BPS_MIN ||
      patch.slippageBps > SLIPPAGE_BPS_MAX
    ) {
      errors.push(
        `slippageBps must be between ${SLIPPAGE_BPS_MIN} and ${SLIPPAGE_BPS_MAX}`,
      );
    } else {
      normalizedPatch.slippageBps = patch.slippageBps;
    }
  }

  if (patch.priceImpactBps !== undefined) {
    if (
      !isFiniteNumber(patch.priceImpactBps) ||
      !Number.isInteger(patch.priceImpactBps)
    ) {
      errors.push("priceImpactBps must be an integer");
    } else if (
      patch.priceImpactBps < PRICE_IMPACT_BPS_MIN ||
      patch.priceImpactBps > PRICE_IMPACT_BPS_MAX
    ) {
      errors.push(
        `priceImpactBps must be between ${PRICE_IMPACT_BPS_MIN} and ${PRICE_IMPACT_BPS_MAX}`,
      );
    } else {
      normalizedPatch.priceImpactBps = patch.priceImpactBps;
    }
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, normalizedPatch };
};

export const resolveEffectiveUserSwapPreferences = (params: {
  chainId?: number | null;
  userGlobalOverride?: UserSwapPreferencesPatch | null;
  portfolioOverride?: UserSwapPreferencesPatch | null;
}): UserSwapPreferencesResolvedWithMetadata => {
  const chainId =
    typeof params.chainId === "number" && Number.isFinite(params.chainId)
      ? params.chainId
      : null;
  const thresholdFloorUsd = getThresholdFloorForChain(chainId);

  const defaults: UserSwapPreferencesResolved = {
    thresholdUsd: getThresholdDefaultForChain(chainId),
    slippageBps: SLIPPAGE_BPS_DEFAULT,
    priceImpactBps: PRICE_IMPACT_BPS_DEFAULT,
  };

  let thresholdUsd = defaults.thresholdUsd;
  let slippageBps = defaults.slippageBps;
  let priceImpactBps = defaults.priceImpactBps;

  let thresholdSource: UserSwapPreferencesFieldSource = "default";
  let slippageSource: UserSwapPreferencesFieldSource = "default";
  let priceImpactSource: UserSwapPreferencesFieldSource = "default";

  const userGlobalOverride = params.userGlobalOverride ?? null;
  if (userGlobalOverride?.thresholdUsd !== undefined) {
    thresholdUsd = normalizeUsd(userGlobalOverride.thresholdUsd);
    thresholdSource = "userGlobal";
  }
  if (userGlobalOverride?.slippageBps !== undefined) {
    slippageBps = userGlobalOverride.slippageBps;
    slippageSource = "userGlobal";
  }
  if (userGlobalOverride?.priceImpactBps !== undefined) {
    priceImpactBps = userGlobalOverride.priceImpactBps;
    priceImpactSource = "userGlobal";
  }

  const portfolioOverride = params.portfolioOverride ?? null;
  if (portfolioOverride?.thresholdUsd !== undefined) {
    thresholdUsd = normalizeUsd(portfolioOverride.thresholdUsd);
    thresholdSource = "portfolio";
  }
  if (portfolioOverride?.slippageBps !== undefined) {
    slippageBps = portfolioOverride.slippageBps;
    slippageSource = "portfolio";
  }
  if (portfolioOverride?.priceImpactBps !== undefined) {
    priceImpactBps = portfolioOverride.priceImpactBps;
    priceImpactSource = "portfolio";
  }

  const thresholdFloorNum = Number(thresholdFloorUsd);
  const resolvedThresholdNum = Number(thresholdUsd);
  const floorApplied = resolvedThresholdNum < thresholdFloorNum;
  if (floorApplied) {
    thresholdUsd = normalizeUsd(thresholdFloorUsd);
    thresholdSource = "chainFloor";
  }

  return {
    defaults,
    userGlobalOverride,
    portfolioOverride,
    resolved: {
      thresholdUsd,
      slippageBps: clamp(slippageBps, SLIPPAGE_BPS_MIN, SLIPPAGE_BPS_MAX),
      priceImpactBps: clamp(
        priceImpactBps,
        PRICE_IMPACT_BPS_MIN,
        PRICE_IMPACT_BPS_MAX,
      ),
    },
    source: {
      thresholdUsd: thresholdSource,
      slippageBps: slippageSource,
      priceImpactBps: priceImpactSource,
    },
    chain: {
      chainId,
      thresholdFloorUsd: normalizeUsd(thresholdFloorUsd),
      floorApplied,
    },
  };
};

export const resolveAssetMinSlippageBps = (params: {
  fromAssetId?: string | null;
  toAssetIds?: readonly string[] | null;
  baseSlippageBps: number;
  maxSlippageBps?: number;
}): number => {
  const candidateIds = new Set<string>();
  if (params.fromAssetId) {
    candidateIds.add(normalizeAssetIdKey(params.fromAssetId));
  }
  if (params.toAssetIds) {
    for (const assetId of params.toAssetIds) {
      candidateIds.add(normalizeAssetIdKey(assetId));
    }
  }

  let resolved = params.baseSlippageBps;
  for (const assetId of candidateIds) {
    const minForAsset = SLIPPAGE_BPS_ASSET_MIN[assetId];
    if (typeof minForAsset === "number") {
      resolved = Math.max(resolved, minForAsset);
    }
  }

  return clamp(resolved, SLIPPAGE_BPS_MIN, params.maxSlippageBps ?? SLIPPAGE_BPS_MAX);
};
